import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Car, ShieldAlert, Baby, Dog, KeyRound, BatteryCharging, Trash2, ScanLine } from "lucide-react";

const MODES = [
  { key: "parking", label: "Parking", desc: "Default mode. Standard alerts.", icon: <Car size={22} /> },
  { key: "emergency", label: "Emergency", desc: "Priority alerts. Faster escalation.", icon: <ShieldAlert size={22} />, danger: true },
  { key: "child", label: "Child onboard", desc: "Treat alerts as critical safety.", icon: <Baby size={22} /> },
  { key: "pet", label: "Pet onboard", desc: "Pet inside — handle with care.", icon: <Dog size={22} /> },
  { key: "valet", label: "Valet", desc: "Time-locked. Restrict driver access.", icon: <KeyRound size={22} /> },
  { key: "ev", label: "EV charging", desc: "Plugged in. Reachable for unplug.", icon: <BatteryCharging size={22} /> },
];

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/vehicles");
      const v = data.find((x) => x.id === id);
      if (!v) throw new Error("Vehicle not found");
      setVehicle(v);
    } catch (err) {
      toast.error(formatApiError(err));
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const setMode = async (mode) => {
    try {
      const body = mode === "valet" ? { mode, expires_in_minutes: 120 } : { mode };
      const { data } = await api.patch(`/vehicles/${id}/mode`, body);
      setVehicle((v) => ({ ...v, ...data }));
      toast.success(`Mode set: ${mode}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    if (!confirm("Delete this vehicle and its card? This cannot be undone.")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Vehicle removed");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !vehicle) {
    return <DashLayout title="Vehicle"><div className="text-gray-500">Loading…</div></DashLayout>;
  }

  const scanUrl = `${window.location.origin}/s/${vehicle.card?.card_code}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=0&qzone=1&data=${encodeURIComponent(scanUrl)}`;

  return (
    <DashLayout title={vehicle.nickname}>
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 mb-8 hover:text-[#002FA7]" data-testid="back-dashboard">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* QR card preview */}
        <aside className="lg:col-span-5">
          <div className="card-flat p-6" data-testid="qr-card-section">
            <div className="label-eyebrow mb-3">Your scannable card</div>
            <div className="qr-card text-black">
              <div className="mt-6 mb-3">
                <div className="label-eyebrow">If you need to reach me</div>
                <div className="font-display font-bold text-lg mt-1">Scan this card</div>
              </div>
              <div className="bg-white border border-gray-300 p-3 inline-block">
                <img src={qrSrc} alt="QR code" width={220} height={220} data-testid="qr-image" />
              </div>
              <div className="font-mono text-xs mt-4 text-gray-700">{vehicle.card?.card_code}</div>
              <div className="font-mono text-[10px] text-gray-500 mt-1 break-all">{scanUrl}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <a href={qrSrc} target="_blank" rel="noreferrer" className="btn btn-ghost text-sm flex-1" data-testid="download-qr">
                <ScanLine size={14} /> Open QR
              </a>
              <button onClick={remove} className="btn btn-ghost text-sm text-[#E63946] border-[#E63946]/30" data-testid="delete-vehicle">
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>

          <div className="card-flat p-5 mt-4" data-testid="vehicle-meta">
            <div className="label-eyebrow mb-2">Vehicle</div>
            <div className="font-mono text-sm space-y-1">
              <div><span className="text-gray-500">plate: </span>{vehicle.plate_number}</div>
              <div><span className="text-gray-500">type: </span>{vehicle.vehicle_type}</div>
              <div><span className="text-gray-500">current_mode: </span>{vehicle.current_mode}</div>
              {vehicle.mode_expires_at && <div><span className="text-gray-500">expires: </span>{new Date(vehicle.mode_expires_at).toLocaleString()}</div>}
            </div>
          </div>
        </aside>

        {/* Modes */}
        <section className="lg:col-span-7">
          <div className="label-eyebrow mb-3">Vehicle mode</div>
          <h2 className="font-display font-bold text-2xl mb-6">How should we treat alerts right now?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MODES.map((m) => {
              const active = vehicle.current_mode === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  className={`p-5 border text-left transition-all ${
                    active
                      ? (m.danger ? "border-[#E63946] bg-[#E63946]/5" : "border-[#002FA7] bg-[#002FA7]/5")
                      : "border-gray-200 bg-white hover:border-gray-400"
                  }`}
                  data-testid={`mode-${m.key}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`${active ? (m.danger ? "text-[#E63946]" : "text-[#002FA7]") : "text-gray-700"}`}>
                      {m.icon}
                    </div>
                    {active && <span className="font-mono text-[10px] tracking-widest uppercase font-bold">Active</span>}
                  </div>
                  <div className="font-display font-bold text-lg">{m.label}</div>
                  <div className="text-sm text-gray-600 mt-1">{m.desc}</div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </DashLayout>
  );
}
